import { Capacitor } from '@capacitor/core';
import { StatusBar, Style } from '@capacitor/status-bar';
import { logger } from './logger';

export type Platform = 'web' | 'android' | 'ios';

/**
 * Detect the current platform using Capacitor's official detection
 * Only detects native platforms when running in actual Capacitor app
 */
export const detectPlatform = (): Platform => {
  // Use Capacitor's official platform detection
  if (Capacitor.isNativePlatform()) {
    const platform = Capacitor.getPlatform();
    if (platform === 'android') return 'android';
    if (platform === 'ios') return 'ios';
  }

  // Always default to web if not running in native Capacitor app
  // This ensures browser dev tools don't trigger mobile payments
  return 'web';
};

/**
 * Check if the current platform should use Stripe payments
 */
export const shouldUseStripePayments = (): boolean => {
  const platform = detectPlatform();
  // Only use Stripe on web platform
  return platform === 'web';
};

/**
 * Check if the current platform should use Google Play Billing
 */
export const shouldUseGooglePlayBilling = (): boolean => {
  const platform = detectPlatform();
  return platform === 'android';
};

/**
 * Check if payments are disabled for the current platform
 */
export const arePaymentsDisabled = (): boolean => {
  const platform = detectPlatform();
  // For now, disable payments on iOS until we implement App Store billing
  return platform === 'ios';
};

/**
 * Get platform-specific payment method name
 */
export const getPaymentMethodName = (): string => {
  const platform = detectPlatform();
  switch (platform) {
    case 'web':
      return 'Stripe';
    case 'android':
      return 'Google Play';
    case 'ios':
      return 'App Store';
    default:
      return 'Unknown';
  }
};

/**
 * Debug platform detection - useful for development
 */
export const debugPlatformDetection = (): void => {
  console.log('🔍 Platform Detection Debug:');
  console.log('  Capacitor.isNativePlatform():', Capacitor.isNativePlatform());
  console.log('  Capacitor.getPlatform():', Capacitor.getPlatform());
  console.log('  Detected platform:', detectPlatform());
  console.log('  Should use Stripe:', shouldUseStripePayments());
  console.log('  Should use Google Play:', shouldUseGooglePlayBilling());
  console.log('  Payment method:', getPaymentMethodName());
};

// Make debug function available globally in development
if (process.env.NODE_ENV === 'development') {
  (window as any).debugPlatform = debugPlatformDetection;
}

/**
 * Initialize platform-specific settings
 * Should be called once on app startup
 */
export const initializePlatform = async (): Promise<void> => {
  const platform = detectPlatform();

  if (platform === 'android' || platform === 'ios') {
    try {
      // Configure status bar for native platforms
      // Use overlay mode so content can extend behind the status bar
      await StatusBar.setOverlaysWebView({ overlay: true });

      // Set status bar style (light text for dark backgrounds)
      await StatusBar.setStyle({ style: Style.Light });

      // Set status bar background color on Android
      if (platform === 'android') {
        await StatusBar.setBackgroundColor({ color: '#4F46E5' });
      }

      logger.debug('Platform initialized', { platform });
    } catch (error) {
      logger.error('Error initializing platform', error);
    }
  }
};

